import { createLowlight } from 'lowlight';
import bash from 'highlight.js/lib/languages/bash';
import css from 'highlight.js/lib/languages/css';
import diff from 'highlight.js/lib/languages/diff';
import go from 'highlight.js/lib/languages/go';
import java from 'highlight.js/lib/languages/java';
import json from 'highlight.js/lib/languages/json';
import php from 'highlight.js/lib/languages/php';
import python from 'highlight.js/lib/languages/python';
import sql from 'highlight.js/lib/languages/sql';
import typescript from 'highlight.js/lib/languages/typescript';
import xml from 'highlight.js/lib/languages/xml';
import yaml from 'highlight.js/lib/languages/yaml';

/**
 * Syntax highlighting for code blocks, in both the editors and the renderer.
 *
 * ── Why a hand-picked list ──────────────────────────────────────────────────
 * `lowlight` is highlight.js with the grammars imported individually.
 * `lowlight/all` pulls in every one of ~190 languages, which is most of a
 * megabyte before compression — on an app that already ships as a single
 * 1.5MB chunk, that is a real cost paid on first load by everyone, to
 * highlight a Dockerfile someone might paste one day. These twelve are what
 * actually turns up in a bug report about this product: the stack it is
 * written in, the things its clients run, a diff, and a config file.
 *
 * Adding one is a one-line import. Do that rather than reaching for `/all`.
 *
 * ── Auto-detection, and its one honest limit ────────────────────────────────
 * A pasted block rarely carries a language, so `CodeBlockLowlight` is left to
 * guess from the registered set. That works well for real source and for JSON.
 *
 * It does NOT work for application logs, and nothing here pretends otherwise:
 * there is no highlight.js grammar for "a log", so a pasted log is detected as
 * whatever it happens to resemble, or as nothing. It still renders as
 * monospace, preserved exactly — which is what a log actually needs — and
 * `logLevelClass` below tints the lines that matter rather than inventing a
 * syntax it does not have.
 */
export const lowlight = createLowlight({
  bash,
  css,
  diff,
  go,
  java,
  json,
  php,
  python,
  sql,
  typescript,
  xml,
  yaml,
});

/** Aliases people actually type in a fence, mapped onto the grammars above. */
lowlight.registerAlias({
  bash: ['sh', 'shell', 'zsh', 'console'],
  typescript: ['ts', 'js', 'javascript', 'tsx', 'jsx'],
  xml: ['html', 'svg'],
  yaml: ['yml'],
});

/**
 * Whether a string is JSON worth reformatting, and the reformatted version.
 *
 * Returns null for anything that is not an object or array at the top level —
 * `JSON.parse("42")` and `JSON.parse("\\"hi\\"")` both succeed, and treating a
 * pasted number as "JSON to prettify" would be an offer to reformat a word.
 */
export function prettyJson(source: string): string | null {
  const trimmed = source.trim();
  if (trimmed === '') return null;
  const first = trimmed[0];
  if (first !== '{' && first !== '[') return null;

  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const pretty = JSON.stringify(parsed, null, 2);
    // Already formatted exactly this way — offering "Prettify" would be a
    // button that visibly does nothing.
    return pretty === trimmed ? null : pretty;
  } catch {
    return null;
  }
}

/**
 * A tint for a log line, by its level.
 *
 * Deliberately crude, and deliberately not called highlighting. It matches the
 * level tokens that nearly every logger emits somewhere near the start of a
 * line, and colours nothing else. The value of a pasted log is finding the one
 * ERROR in ninety lines of INFO, and that is the whole job.
 *
 * Returns null when a line carries no level, which is most of them.
 */
export function logLevelClass(line: string): string | null {
  // Bounded to the first 200 characters: a stack trace line can be very long,
  // and the level is always near the front. Without the bound, a regex over a
  // 4,000-line paste is scanning the whole thing for nothing.
  const head = line.slice(0, 200);
  if (/\b(ERROR|FATAL|CRITICAL|SEVERE)\b/.test(head)) return 'text-danger';
  if (/\b(WARN|WARNING)\b/.test(head)) return 'text-warn';
  if (/\b(DEBUG|TRACE)\b/.test(head)) return 'text-faint';
  return null;
}

/**
 * Whether a block reads as a log rather than as source.
 *
 * One cheap test: do several lines carry a level token. Source code does not,
 * and a log that carries none is indistinguishable from prose anyway — in
 * which case it falls through to ordinary highlighting and loses nothing.
 */
export function looksLikeLog(source: string): boolean {
  const lines = source.split('\n').slice(0, 50);
  const levelled = lines.filter((line) => logLevelClass(line) !== null).length;
  return levelled >= 2;
}

/**
 * Whether a string is JSON at all, formatted or not.
 *
 * Distinct from `prettyJson`, which returns null when there is nothing to
 * reformat — and "already laid out exactly like this" is one of the cases it
 * calls nothing. A block that is already formatted still belongs in a code
 * block rather than a paragraph, so the paste handler needs both answers.
 */
export function isFormattedJson(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return false;
  try {
    JSON.parse(trimmed);
    return true;
  } catch {
    return false;
  }
}
